import { Check } from 'lucide-react';
import { type ComponentType, type SVGProps } from 'react';

import { cn } from '@/lib/shadcn/utils';
import type { CandidateFinding } from '@/modules/candidate-detail/types';

interface CandidateFindingListProps {
  emptyText: string;
  findings: CandidateFinding[];
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
}

const sourceLabels: Record<string, string> = {
  profile: 'Profile',
  prescreen: 'Pre-screen',
};

/** Findings as a hairline list: summary, explanation, quiet source tags. */
export function CandidateFindingList({
  emptyText,
  findings,
  icon: Icon = Check,
  iconClassName = 'text-semantic-560',
}: CandidateFindingListProps) {
  if (findings.length === 0) {
    return (
      <p className="text-muted-foreground border-y py-3 text-[13px]">
        {emptyText}
      </p>
    );
  }

  return (
    <ul className="divide-border flex flex-col divide-y border-y">
      {findings.map((finding, index) => {
        const hasDistinctExplanation =
          finding.explanation.trim().toLocaleLowerCase() !==
          finding.summary.trim().toLocaleLowerCase();
        const sources = [...new Set(finding.sources)];

        return (
          <li
            className="flex items-start gap-3 py-3"
            key={`${finding.summary}-${index}`}
          >
            <Icon
              aria-hidden
              className={cn('mt-0.5 size-4 shrink-0', iconClassName)}
            />
            <div className="flex min-w-0 flex-col gap-1">
              <p className="font-medium">{finding.summary}</p>
              {hasDistinctExplanation && (
                <p className="text-muted-foreground max-w-[64ch] text-[13px] leading-[18px]">
                  {finding.explanation}
                </p>
              )}
              {sources.length > 0 && (
                <p className="text-xs text-neutral-500">
                  {sources
                    .map((source) => sourceLabels[source] ?? 'Evidence')
                    .join(' · ')}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
